import { AuthBrandLogo } from '../../features/auth/AuthBrandLogo';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchOrganizations } from '../../features/organizations/api';
import type { Organization } from '../../features/organizations/types';
import { brandConfig } from '../../config/brands';
import { absoluteAppUrl } from '../../utils/navigation';
import './OrganizationSearchPage.css';

export default function OrganizationSearchPage() {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [userDomain, setUserDomain] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [isFreeDomain, setIsFreeDomain] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await searchOrganizations();

      // 遷移先 /org/:slug は slug でしか解決できない。API 側でも slug 無しは除外しているが、
      // name で代用して 404 に飛ばさないよう念のためここでも落とす。
      setOrganizations(response.organizations.filter((org) => !!org.slug));
      setUserDomain(response.user_domain);

      if (response.message) {
        setInfoMessage(response.message);
      }
      if (response.is_free_domain) {
        setIsFreeDomain(true);
      }
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
      if (err.response?.status === 401) {
        setError('認証が必要です。ログインしてください。');
      } else {
        setError(err.message || '組織の検索に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrganizationClick = (slug: string | undefined) => {
    if (!slug) return;
    navigate(`/org/${slug}`);
  };

  const handleBackToOnboarding = () => {
    navigate('/onboarding');
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="loading-state">組織を検索中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <AuthBrandLogo />
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">組織を検索</h1>
            {userDomain && (
              <p className="auth-subtitle">
                <span className="user-email-badge">@{userDomain}</span> ドメインの組織
              </p>
            )}
          </div>

          <div className="auth-content">
            {/* エラーメッセージ */}
            {error && (
              <div className="auth-message auth-message-error">
                {error}
              </div>
            )}

            {/* 情報メッセージ（無料メールドメインの場合など） */}
            {infoMessage && (
              <div className={isFreeDomain ? "auth-message auth-message-warning" : "info-text"}>
                {infoMessage}
              </div>
            )}

            {/* 無料メールドメインの場合の参加方法タイル */}
            {isFreeDomain && (
              <div className="join-methods">
                <h2 className="join-methods-title">無料メールアドレスで組織に参加する方法</h2>
                <div className="join-methods-tiles">
                  <div className="join-method-tile">
                    <span className="join-method-label">方法1</span>
                    <p className="join-method-description">
                      1. {brandConfig.serviceName}をすでに利用中の管理者にあなたのメールアドレスを伝えて招待してもらう
                    </p>
                    <div className="join-method-hint">
                      <p className="join-method-hint-text">管理者はメニューのユーザー管理ページからメールアドレスを入力して招待します</p>
                    </div>
                    <p className="join-method-description">
                      2. 招待メールが届いたらリンクをクリックして参加
                    </p>
                  </div>
                  <div className="join-method-tile">
                    <span className="join-method-label">方法2</span>
                    <p className="join-method-description">
                      1. 組織内のメンバーにスラッグURLを教えてもらう
                    </p>
                    <div className="join-method-hint">
                      <p className="join-method-hint-text">スラッグはログイン後の組織情報ページに記載されています</p>
                      <p className="join-method-hint-label">スラッグの例</p>
                      <p className="join-method-hint-url">
                        {absoluteAppUrl('/org')}/<code>sample-company</code>
                      </p>
                    </div>
                    <p className="join-method-description">
                      2. スラッグURLに直接アクセスして参加依頼を出す
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 組織リスト */}
            {!infoMessage && (
              <>
                {organizations.length > 0 ? (
                  <div className="org-list">
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        className="org-card"
                        onClick={() => handleOrganizationClick(org.slug)}
                      >
                        {org.logo_url && (
                          <img
                            src={org.logo_url}
                            alt={`${org.display_name}のロゴ`}
                            className="org-card-logo"
                          />
                        )}
                        <div className="org-card-content">
                          <h3 className="org-card-title">{org.display_name}</h3>
                          <p className="org-card-slug">/{org.slug}</p>
                          {org.description && (
                            <p className="org-card-description">{org.description}</p>
                          )}
                        </div>
                        <svg className="org-card-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    {userDomain} ドメインの組織が見つかりませんでした
                  </div>
                )}
              </>
            )}

            {/* 戻るボタン */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBackToOnboarding}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
